/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  dataPixels,
  calcRotatedObjectPositionAndSize,
  useUnits,
} from '@web-stories-wp/units';
/**
 * Internal dependencies
 */

import clamp from '../../../../utils/clamp';
import getTextHeightResizeUpdates from '../../../../utils/getTextHeightResizeUpdates';
import { isTextBoxAttachment } from '../../../../utils/textBoxAttachment';
import { getDefinitionForType } from '../../../../elements';
import usePresubmitHandler from '../../../form/usePresubmitHandler';
import { MIN_MAX } from './constants';

const clampDimension = (value, { MIN, MAX }) => {
  const clampedMin = Math.max(value, MIN);
  return MAX === undefined ? clampedMin : Math.min(clampedMin, MAX);
};

function usePresubmitHandlers(lockAspectRatio) {
  const { dataToEditorX, dataToEditorY } = useUnits((state) => ({
    dataToEditorX: state.actions.dataToEditorX,
    dataToEditorY: state.actions.dataToEditorY,
  }));

  // Recalculate width/height if ratio locked.
  usePresubmitHandler(
    (
      newElement,
      { width: newWidth, height: newHeight },
      { width: oldWidth, height: oldHeight }
    ) => {
      const { type } = newElement;

      const isResizeWidth = Boolean(newWidth);
      const isResizeHeight = Boolean(newHeight);
      if (!isResizeWidth && !isResizeHeight) {
        return null;
      }

      // Use resize rules if available.
      const { updateForResizeEvent } = getDefinitionForType(type);
      if (updateForResizeEvent) {
        const direction = [isResizeWidth ? 1 : 0, isResizeHeight ? 1 : 0];
        const resizeUpdates = updateForResizeEvent({
          element: newElement,
          direction,
          newWidth: clampDimension(newWidth, MIN_MAX.WIDTH),
          newHeight: clampDimension(newHeight, MIN_MAX.HEIGHT),
          dataToEditorX,
          dataToEditorY,
        });

        if (isResizeWidth && !isResizeHeight && resizeUpdates?.height) {
          return {
            ...resizeUpdates,
            ...getTextHeightResizeUpdates(
              { ...newElement, height: oldHeight },
              resizeUpdates.height
            ),
          };
        }

        return resizeUpdates;
      }

      // Fallback to ratio.
      if (lockAspectRatio) {
        const ratio = oldWidth / oldHeight;
        if (!isResizeWidth) {
          return {
            width: clampDimension(dataPixels(newHeight * ratio), MIN_MAX.WIDTH),
          };
        }
        if (!isResizeHeight) {
          return {
            height: clampDimension(
              dataPixels(newWidth / ratio),
              MIN_MAX.HEIGHT
            ),
          };
        }
      }

      return null;
    },
    [dataToEditorX, dataToEditorY, lockAspectRatio]
  );

  usePresubmitHandler(({ rotationAngle: newRotationAngle }) => {
    return {
      rotationAngle: newRotationAngle % 360,
    };
  }, []);

  usePresubmitHandler(
    ({ rotationAngle: na, x: nx, y: ny, width: nw, height: nh }) => {
      const newDims = calcRotatedObjectPositionAndSize(na, nx, ny, nw, nh);
      const newXOffset = nx - newDims.x;
      const newYOffset = ny - newDims.y;
      return {
        x: clamp(nx, {
          MIN: MIN_MAX.X.MIN + newXOffset - newDims.width,
          MAX: MIN_MAX.X.MAX + newXOffset,
        }),
        y: clamp(ny, {
          MIN: MIN_MAX.Y.MIN + newYOffset - newDims.height,
          MAX: MIN_MAX.Y.MAX + newYOffset,
        }),
      };
    },
    []
  );

  usePresubmitHandler((element, updatedProperties) => {
    const preserveHeightPrecision =
      element.type === 'text' &&
      element.verticalAnchor === 'center' &&
      !isTextBoxAttachment(element.textBoxAttachment) &&
      !Object.prototype.hasOwnProperty.call(updatedProperties, 'height');
    // Rounding height on an unrelated inspector edit shifts the centre before
    // the text display restores its fractional measured height.
    return {
      height: clampDimension(
        preserveHeightPrecision ? element.height : dataPixels(element.height),
        MIN_MAX.HEIGHT
      ),
    };
  }, []);

  usePresubmitHandler(({ width: newWidth }) => {
    return {
      width: clampDimension(dataPixels(newWidth), MIN_MAX.WIDTH),
    };
  }, []);
}

export default usePresubmitHandlers;
